import React from 'react';
import { Loader } from 'lucide-react';

/**
 * Button - Fully-featured premium button component
 *
 * Props:
 *  - variant: 'primary' | 'secondary' | 'danger' | 'ghost' | 'outline' | 'success' | 'warning'
 *  - size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 *  - leftIcon: ReactNode
 *  - rightIcon: ReactNode
 *  - isLoading: boolean
 *  - loadingText: string
 *  - disabled: boolean
 *  - fullWidth: boolean
 *  - pill: boolean (fully rounded)
 *  - onClick: function
 *  - type: 'button' | 'submit' | 'reset'
 *  - id: string
 *  - className: string
 *  - children: ReactNode
 */
export default function Button({
  variant = 'primary',
  size = 'md',
  leftIcon,
  rightIcon,
  isLoading = false,
  loadingText,
  disabled = false,
  fullWidth = false,
  pill = false,
  onClick,
  type = 'button',
  id,
  className = '',
  children,
  ...rest
}) {
  const isDisabled = disabled || isLoading;

  return (
    <button
      type={type}
      id={id}
      className={[
        'ui-btn',
        `ui-btn--${variant}`,
        `ui-btn--${size}`,
        fullWidth ? 'ui-btn--full' : '',
        pill ? 'ui-btn--pill' : '',
        isLoading ? 'ui-btn--loading' : '',
        isDisabled ? 'ui-btn--disabled' : '',
        className,
      ].filter(Boolean).join(' ')}
      onClick={onClick}
      disabled={isDisabled}
      aria-busy={isLoading}
      aria-label={typeof children === 'string' ? children : undefined}
      {...rest}
    >
      {isLoading ? (
        <>
          <Loader size={15} className="ui-btn-spinner" />
          {loadingText && <span>{loadingText}</span>}
        </>
      ) : (
        <>
          {leftIcon && <span className="ui-btn-icon ui-btn-icon-left">{leftIcon}</span>}
          {children && <span className="ui-btn-text">{children}</span>}
          {rightIcon && <span className="ui-btn-icon ui-btn-icon-right">{rightIcon}</span>}
        </>
      )}
    </button>
  );
}

/**
 * ButtonGroup - Groups multiple buttons together
 *
 * Props:
 *  - children: ReactNode (should be Button components)
 *  - attached: boolean (attached pill group)
 *  - className: string
 */
export function ButtonGroup({ children, attached = false, className = '' }) {
  return (
    <div className={`ui-btn-group ${attached ? 'ui-btn-group--attached' : ''} ${className}`}>
      {children}
    </div>
  );
}

/**
 * IconButton - Square icon-only button
 */
export function IconButton({
  icon,
  variant = 'ghost',
  size = 'md',
  disabled = false,
  isLoading = false,
  onClick,
  title,
  id,
  className = '',
  ...rest
}) {
  return (
    <button
      type="button"
      id={id}
      className={[
        'ui-icon-btn',
        `ui-icon-btn--${variant}`,
        `ui-icon-btn--${size}`,
        isLoading ? 'ui-icon-btn--loading' : '',
        className,
      ].filter(Boolean).join(' ')}
      onClick={onClick}
      disabled={disabled || isLoading}
      title={title}
      aria-label={title}
      {...rest}
    >
      {isLoading ? <Loader size={15} className="ui-btn-spinner" /> : icon}
    </button>
  );
}
